import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Boxes, ListFilter, Plus, Target, Trash2 } from "lucide-react";
import type { CutPiece, PipeRun, Spool } from "@/hooks/use-pipe-sketch";

export interface SpoolStat {
  pieces: number;
  feet: number;
  weight: number;
  welds: number;
  supports: number;
}

interface Props {
  spools: Spool[];
  runs: PipeRun[];
  cutList: CutPiece[];
  activeSpool: string | null;
  /** rolled-up quantities per spool id, plus "unassigned" */
  stats?: Record<string, SpoolStat>;
  /** which spool the quantity panels are scoped to */
  filterSpool?: string;
  onFilterSpool?: (id: string) => void;
  formatFtIn: (inches: number) => string;
  onSetActiveSpool: (id: string | null) => void;
  onAddSpool: (name?: string) => void;
  onRename: (id: string, patch: Partial<Pick<Spool, "mark" | "name">>) => void;
  onRemove: (id: string) => void;
  onAssign: (runId: string, spoolId: string | null) => void;
}

const EMPTY_STAT: SpoolStat = { pieces: 0, feet: 0, weight: 0, welds: 0, supports: 0 };

const SpoolManager = ({
  spools,
  runs,
  cutList,
  activeSpool,
  stats,
  filterSpool = "all",
  onFilterSpool,
  formatFtIn,
  onSetActiveSpool,
  onAddSpool,
  onRename,
  onRemove,
  onAssign,
}: Props) => {
  const [newName, setNewName] = useState("");

  const runsIn = (id: string | null) => runs.filter((r) => (r.spool ?? null) === id);
  const statOf = (id: string) => stats?.[id] ?? EMPTY_STAT;
  const lengthOf = (id: string | null) => {
    const ids = new Set(runsIn(id).map((r) => r.id));
    return cutList.filter((c) => ids.has(c.runId)).reduce((s, c) => s + c.cutLength, 0);
  };
  const piecesOf = (id: string | null) => {
    const ids = new Set(runsIn(id).map((r) => r.id));
    return cutList.filter((c) => ids.has(c.runId)).length;
  };

  const unassigned = runsIn(null);


  return (
    <div className="space-y-4 p-3">
      <section className="space-y-2">
        <h3 className="text-sm font-semibold">Fabrication spools</h3>
        <p className="text-xs text-muted-foreground">
          Group runs into shippable spools. New pipe you draw drops into the spool marked as
          the target.
        </p>

        <div className="flex gap-2">
          <Input
            value={newName}
            placeholder="Spool name (optional)"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onAddSpool(newName);
                setNewName("");
              }
            }}
            className="h-8"
          />
          <Button
            size="sm"
            onClick={() => {
              onAddSpool(newName);
              setNewName("");
            }}
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Spool
          </Button>
        </div>
      </section>

      <section className="space-y-2">
        {spools.length === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            No spools yet — everything is drawn as one loose assembly.
          </p>
        ) : (
          spools.map((sp) => {
            const active = activeSpool === sp.id;
            return (
              <div
                key={sp.id}
                className={`space-y-2 rounded-md border p-2 ${
                  active ? "border-primary bg-primary/5" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  <Boxes className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <Input
                    value={sp.mark}
                    onChange={(e) => onRename(sp.id, { mark: e.target.value })}
                    className="h-7 w-20 font-mono text-xs"
                  />
                  <Input
                    value={sp.name}
                    onChange={(e) => onRename(sp.id, { name: e.target.value })}
                    className="h-7 flex-1 text-xs"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    aria-label={`Delete spool ${sp.mark}`}
                    onClick={() => onRemove(sp.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-x-3 text-[11px] text-muted-foreground">
                  <span>
                    {runsIn(sp.id).length} run{runsIn(sp.id).length === 1 ? "" : "s"} ·{" "}
                    {piecesOf(sp.id)} pieces
                  </span>
                  <span className="text-right font-mono">{formatFtIn(lengthOf(sp.id))}</span>
                  <span>
                    {statOf(sp.id).welds} joints · {statOf(sp.id).supports} supports
                  </span>
                  <span className="text-right font-mono">
                    {statOf(sp.id).weight.toFixed(0)} lb
                  </span>
                </div>
                <div className="flex items-center justify-end gap-1">
                  <Button
                    size="sm"
                    variant={filterSpool === sp.id ? "secondary" : "ghost"}
                    className="h-6 px-2 text-[11px]"
                    onClick={() => onFilterSpool?.(filterSpool === sp.id ? "all" : sp.id)}
                  >
                    <ListFilter className="mr-1 h-3 w-3" />
                    {filterSpool === sp.id ? "Scoped" : "Scope"}
                  </Button>
                  <Button
                    size="sm"
                    variant={active ? "default" : "outline"}
                    className="h-6 px-2 text-[11px]"
                    onClick={() => onSetActiveSpool(active ? null : sp.id)}
                  >
                    <Target className="mr-1 h-3 w-3" />
                    {active ? "Drawing here" : "Draw into"}
                  </Button>
                </div>

              </div>
            );
          })
        )}
      </section>

      <section className="space-y-2 border-t pt-3">
        <h3 className="text-sm font-semibold">Runs</h3>
        {runs.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nothing drawn yet.</p>
        ) : (
          <div className="space-y-1">
            {runs.map((r, i) => (
              <div key={r.id} className="flex items-center gap-2 rounded-md border p-2">
                <span className="w-16 shrink-0 text-xs font-medium">Run {i + 1}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {r.size} · {Math.max(0, r.pts.length - 1)} seg
                </span>
                <Select
                  value={r.spool ?? "none"}
                  onValueChange={(v) => onAssign(r.id, v === "none" ? null : v)}
                >
                  <SelectTrigger className="ml-auto h-7 w-32 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {spools.map((sp) => (
                      <SelectItem key={sp.id} value={sp.id}>
                        {sp.mark}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
        {unassigned.length > 0 && spools.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            {unassigned.length} run{unassigned.length === 1 ? "" : "s"} still unassigned.
          </p>
        )}
      </section>
    </div>
  );
};

export default SpoolManager;
